import './App.css';
import Home from './Pages/Home';
import TermsOfUse from './Pages/TermsOfUse/TermsOfUse';
import PrivacyPolicy from './Pages/PrivacyPolicy/PrivacyPolicy';
import RefundPolicy from './Pages/RefundPolicy/RefundPolicy';
import PaymentPolicy from './Pages/PaymentPolicy/PaymentPolicy';
import AUP from './Pages/AUP/AUP';
import CookiesPolicy from './Pages/CookiesPolicy/CookiesPolicy';
import Support from './Pages/Support/Support';
import ZoomIntegration from './Pages/ZoomIntegration/ZoomIntegration';
import SetupApp from './Pages/SetupApp/SetupApp';
import FeaturesOverview from './Pages/Features/FeaturesOverview';
import FeaturePage from './Pages/Features/FeaturePage';
import BlogIndex from './Pages/Blog/BlogIndex';
import BlogPost from './Pages/Blog/BlogPost';
import ComingSoonPage from './Pages/Features/ComingSoonPage';
import PricingPage from './Pages/Pricing/Pricing';
import BusinessesOverview from './Pages/Businesses/BusinessesOverview';
import BusinessCategoryPage from './Pages/Businesses/BusinessCategoryPage';
import AboutPage from './Pages/About/AboutPage';
import GuidePage from './Pages/Guides/GuidePage';
import AOS from 'aos';
import "aos/dist/aos.css";
import { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import HomeRedirect from './Components/HomeRedirect/HomeRedirect';
import {
  TEMP_CRM_PIVOT,
  isHiddenFeaturePath,
  shouldRedirectHomeFromLanding,
} from './config/tempCrmPivot';
import { useTranslation } from 'react-i18next';
import { captureGclidFromUrl, captureFbclidFromUrl, trackPageView, trackStartOnboardingClick } from './utils/track';
import { initMetaPixel } from './utils/metaPixel';
import { trackPageView as trackBackendPageView, trackScroll, trackTryNowClick, trackBookDemoClick } from './utils/analytics';
import { getStoredHeroPhone, noteSocialLanding } from './utils/portalUrl';
import { useShowHebrew } from './hooks/useShowHebrew';
import { readCachedCountrySync } from './utils/ipDetection';
import { basenameFor, stripLangPrefix } from './utils/locale';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Fires a GTM `page_view` event on initial mount and on every client-side
 * route change. Also captures the GCLID once at app startup so it's available
 * across the session (survives further navigation, hand-off to portal, etc.).
 *
 * Plus: installs a global click delegate that catches ANY outbound click to
 * the onboarding URL (portal.stylon.co/onboarding) and fires the GTM
 * `start_onboarding_click` event. CTAs are scattered across many components
 * with inconsistent inline handlers; delegation guarantees we never miss one.
 * The CTA source is read from the link's `utm_content` query param (set by
 * getOnboardingUrl per call site: hero, navbar, pricing_team, etc.).
 */
function TrackPageViews() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  // Capture GCLID and FBCLID once at mount, and init the platform Meta Pixel
  // (env-gated via REACT_APP_META_PIXEL_ID; no-op when unset).
  useEffect(() => {
    captureGclidFromUrl();
    captureFbclidFromUrl();
    // The tab's latest Social touch: kept from a Stylon post's link, taken away by a later paid or tagged landing.
    noteSocialLanding(window.location.search);
    initMetaPixel(process.env.REACT_APP_META_PIXEL_ID);
  }, []);

  // Global click listener: catch outbound clicks to known conversion
  // destinations and fire both the GTM event (ad-tech feedback) and
  // the backend event (admin dashboard funnel). Single delegate so any
  // future CTA — anywhere on the site — is automatically tracked, as
  // long as it points at one of the recognised destinations. No
  // per-component instrumentation needed.
  //
  // Recognised destinations:
  //   • portal.stylon.co/onboarding → try_now_click + start_onboarding_click
  //     The CTA source is read from utm_content (set by getOnboardingUrl
  //     per call site: hero, navbar, pricing_team, contact, ...).
  //   • stylon.online/stylon/add-appointment → book_demo_click
  //     The "Book Demo" button on BookDemoCTA + DemoModal — different
  //     conversion funnel from "Try Now", so it gets its own event.
  useEffect(() => {
    function onClick(e) {
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (/portal\.stylon\.co\/onboarding/i.test(href)) {
        let source = 'unknown';
        try {
          const url = new URL(href, window.location.origin);
          source = url.searchParams.get('utm_content') || 'unknown';
          // Re-stamp the hero phone from the LATEST localStorage value
          // at click-time. The href was computed during a previous
          // render, and React state updates (which trigger renders)
          // run after browser-paint — so a user who typed the last
          // digit and clicked "Start Now" immediately would otherwise
          // navigate with a phone missing the final digit. Reading
          // here, after every keystroke has been flushed to storage,
          // guarantees the URL carries what the user actually has in
          // the field. Same logic also fixes navbar / FAQ / pricing
          // CTAs that don't re-render on hero typing.
          const heroPhone = getStoredHeroPhone();
          // Count digits across ASCII + Arabic-Indic + Persian so an
          // Arabic-keyboard user's phone passes the plausibility gate.
          const digitCount = heroPhone ? (heroPhone.match(/[0-9٠-٩۰-۹]/g) || []).length : 0;
          if (digitCount >= 9) {
            url.searchParams.set('phone', heroPhone);
          } else {
            url.searchParams.delete('phone');
          }
          a.href = url.toString();
        } catch (_) {}
        trackStartOnboardingClick(source);
        trackTryNowClick(source);
        return;
      }
      if (/stylon\.online\/stylon\/add-appointment/i.test(href)) {
        // Source = either utm_content (if the link sets one) or the
        // current page key — so "Book Demo" from the home BookDemoCTA
        // shows up as "home", from the modal as "demo_modal" once the
        // modal sets utm_content, etc.
        let source = 'cta_block';
        try {
          const url = new URL(href, window.location.origin);
          source = url.searchParams.get('utm_content') || source;
        } catch (_) {}
        trackBookDemoClick(source);
      }
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Fire page_view per route change. Defer to next tick so document.title (set by
  // React-Helmet a microtask after route mount) reflects the new page when read.
  // Two pipelines fire here on purpose:
  //  - GTM trackPageView → GA4 / Google Ads
  //  - Backend trackBackendPageView → platform_analytics_event → admin dashboard
  // Keeping them in one effect guarantees the two pipelines never drift.
  useEffect(() => {
    const id = setTimeout(() => {
      trackPageView({ path: pathname, lang: i18n.language });
      trackBackendPageView(i18n.language);
    }, 0);
    return () => clearTimeout(id);
  }, [pathname, i18n.language]);

  // Scroll-depth milestones (25/50/75/100%) — re-armed on every route
  // change so per-page depth ends up in the `page` field on each event
  // and the dashboard can show "60% of visitors to /for/barber-shops
  // scrolled past 75%". Lifted out of Pages/Home so every route
  // (including /for/*, /features/*, /pricing) is covered.
  useEffect(() => {
    const milestones = [25, 50, 75, 100];
    const sent = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.round((scrollTop / scrollHeight) * 100);
      milestones.forEach((m) => {
        if (pct >= m && !sent.has(m)) {
          sent.add(m);
          trackScroll(m);
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  return null;
}

function AppContent() {
  const { i18n } = useTranslation();
  const showHebrew = useShowHebrew();

  useEffect(() => {
    AOS.init({ duration: 400, easing: 'ease-out', once: true });
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = (i18n.language === 'ar' || i18n.language === 'he') ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // Hebrew is Israel-only here. A non-IL visitor on the /he basename (e.g. a
  // bookmark) is redirected to the /en equivalent — but only once the IP is
  // actually known, so we never redirect on the uncertain first paint.
  useEffect(() => {
    if (!window.location.pathname.startsWith('/he')) return;
    if (showHebrew) return;
    if (!readCachedCountrySync()) return;
    const logical = stripLangPrefix(window.location.pathname) || '/';
    const target = `${basenameFor('en')}${logical === '/' ? '/' : logical}${window.location.search}${window.location.hash}`;
    window.location.assign(target);
  }, [showHebrew]);

  return (
    <>
    <ScrollToTop />
    <TrackPageViews />
    <Routes>
      <Route path="/" element={shouldRedirectHomeFromLanding() ? <HomeRedirect /> : <Home />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/payment-policy" element={<PaymentPolicy />} />
      <Route path="/aup" element={<AUP />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/zoom-integration" element={<ZoomIntegration />} />
      <Route path="/setup-app" element={<SetupApp />} />
      <Route path="/features" element={<FeaturesOverview />} />
      <Route path="/features/online-booking" element={isHiddenFeaturePath('/features/online-booking') ? <Navigate to="/features/crm" replace /> : <FeaturePage featureKey="booking" />} />
      <Route path="/features/crm" element={<FeaturePage featureKey="crm" />} />
      <Route path="/features/website" element={<FeaturePage featureKey="website" />} />
      <Route path="/features/zoom" element={<FeaturePage featureKey="zoom" />} />
      <Route path="/features/reminders" element={isHiddenFeaturePath('/features/reminders') ? <Navigate to="/features/crm" replace /> : <FeaturePage featureKey="reminders" />} />
      <Route path="/features/team" element={<FeaturePage featureKey="team" />} />
      <Route path="/features/payments" element={<FeaturePage featureKey="payments" />} />
      <Route path="/features/appointments" element={isHiddenFeaturePath('/features/appointments') ? <Navigate to="/features/crm" replace /> : <FeaturePage featureKey="appointments" />} />
      <Route path="/features/notifications" element={<FeaturePage featureKey="notifications" />} />
      <Route path="/features/discounts" element={<FeaturePage featureKey="discounts" />} />
      <Route path="/features/gallery" element={<FeaturePage featureKey="gallery" />} />
      <Route path="/features/profile" element={<FeaturePage featureKey="profile" />} />
      <Route path="/features/services" element={isHiddenFeaturePath('/features/services') ? <Navigate to="/features/crm" replace /> : <FeaturePage featureKey="services" />} />
      <Route path="/features/calendar" element={isHiddenFeaturePath('/features/calendar') ? <Navigate to="/features/crm" replace /> : <FeaturePage featureKey="calendar" />} />
      <Route path="/features/roles" element={<FeaturePage featureKey="roles" />} />
      <Route path="/features/qr" element={<FeaturePage featureKey="qr" />} />
      <Route path="/features/app" element={<FeaturePage featureKey="app" />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/for" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessesOverview />} />
      <Route path="/for/hair-salons" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="hair" />} />
      <Route path="/for/barber-shops" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="barber" />} />
      <Route path="/for/nail-salons" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="nails" />} />
      <Route path="/for/spa" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="spa" />} />
      <Route path="/for/massage" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="massage" />} />
      <Route path="/for/med-spa" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="medspa" />} />
      <Route path="/for/fitness" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="fitness" />} />
      <Route path="/for/tanning" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="tanning" />} />
      <Route path="/for/tattoo" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="tattoo" />} />
      <Route path="/for/pet-grooming" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="pet" />} />
      <Route path="/for/dental" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="dental" />} />
      <Route path="/for/clinics" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="clinics" />} />
      <Route path="/for/beauty" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="beauty" />} />
      <Route path="/for/online" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="online" />} />
      <Route path="/for/other" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="other" />} />
      {/* Published by the 2026-09 expansion. Every one of these is in
          src/seo/routes.js too, and the drift test fails if it is not. */}
      <Route path="/features/dental-charting" element={<FeaturePage featureKey="dental-charting" />} />
      <Route path="/features/treatment-areas" element={<FeaturePage featureKey="treatment-areas" />} />
      <Route path="/features/injectables" element={<FeaturePage featureKey="injectables" />} />
      <Route path="/features/records" element={<FeaturePage featureKey="records" />} />
      <Route path="/features/treatment-plans" element={<FeaturePage featureKey="treatment-plans" />} />
      <Route path="/features/agreements" element={<FeaturePage featureKey="agreements" />} />
      <Route path="/features/courses" element={<FeaturePage featureKey="courses" />} />
      <Route path="/features/shop" element={<FeaturePage featureKey="shop" />} />
      <Route path="/features/pos" element={<FeaturePage featureKey="pos" />} />
      <Route path="/features/auto-ads" element={<FeaturePage featureKey="auto-ads" />} />
      <Route path="/features/hospitality" element={<FeaturePage featureKey="hospitality" />} />
      <Route path="/features/rooms" element={<FeaturePage featureKey="rooms" />} />
      <Route path="/features/group-booking" element={<FeaturePage featureKey="group-booking" />} />
      <Route path="/features/messages" element={<FeaturePage featureKey="messages" />} />

      <Route path="/for/laser-hair-removal" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="laser" />} />
      <Route path="/for/aesthetic-clinics" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="aesthetic" />} />
      <Route path="/for/academies" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="academies" />} />
      <Route path="/for/vacation-rentals" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="rentals" />} />
      <Route path="/for/physiotherapy" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="physio" />} />
      <Route path="/for/nutrition" element={TEMP_CRM_PIVOT ? <Navigate to="/features/crm" replace /> : <BusinessCategoryPage categoryKey="nutrition" />} />

      <Route path="/guides" element={<GuidePage />} />

      {/* The journal. One literal Route per URL on purpose: routes.drift.test.js
          reads `path="..."` out of this file and compares it with src/seo/routes.js
          in BOTH directions, and a generated list would be invisible to it — which
          is exactly how /features/marketing once shipped live and unindexed.
          Adding a post is src/blog/posts.js plus one line here. */}
      <Route path="/blog" element={<BlogIndex />} />
      {/* Only categories that HAVE posts get a route. An empty category page is
          the same defect the storefront had — a page that names a set and lists
          nobody — and src/blog/posts.js already refuses to route one, so this
          list grows by a line when a category's first post publishes. */}
      <Route path="/blog/category/verticals" element={<BlogIndex category="verticals" />} />
      <Route path="/blog/category/compare" element={<BlogIndex category="compare" />} />
      <Route path="/blog/category/grow" element={<BlogIndex category="grow" />} />
      <Route path="/blog/category/capabilities" element={<BlogIndex category="capabilities" />} />
      <Route path="/blog/all-in-one-vs-appointment-apps" element={<BlogPost slug="all-in-one-vs-appointment-apps" />} />
      <Route path="/blog/run-a-property-on-one-system" element={<BlogPost slug="run-a-property-on-one-system" />} />
      <Route path="/blog/deposits-and-the-empty-chair" element={<BlogPost slug="deposits-and-the-empty-chair" />} />
      <Route path="/blog/why-whatsapp-is-not-enough" element={<BlogPost slug="why-whatsapp-is-not-enough" />} />
      <Route path="/blog/the-customers-you-already-have" element={<BlogPost slug="the-customers-you-already-have" />} />
      <Route path="/blog/a-page-people-trust" element={<BlogPost slug="a-page-people-trust" />} />
      <Route path="/blog/what-reviews-are-really-for" element={<BlogPost slug="what-reviews-are-really-for" />} />
      <Route path="/blog/a-dental-practice-in-one-place" element={<BlogPost slug="a-dental-practice-in-one-place" />} />
      <Route path="/blog/a-treatment-that-takes-three-visits" element={<BlogPost slug="a-treatment-that-takes-three-visits" />} />
      <Route path="/blog/the-cost-of-a-no-show" element={<BlogPost slug="the-cost-of-a-no-show" />} />
      <Route path="/blog/selling-products-beside-the-chair" element={<BlogPost slug="selling-products-beside-the-chair" />} />
      <Route path="/blog/teaching-what-you-know" element={<BlogPost slug="teaching-what-you-know" />} />
      <Route path="/blog/a-clinic-that-keeps-its-own-records" element={<BlogPost slug="a-clinic-that-keeps-its-own-records" />} />
      <Route path="/blog/two-chairs-one-diary" element={<BlogPost slug="two-chairs-one-diary" />} />
      <Route path="/blog/getting-paid-before-they-arrive" element={<BlogPost slug="getting-paid-before-they-arrive" />} />
      <Route path="/blog/the-desk-is-a-till-too" element={<BlogPost slug="the-desk-is-a-till-too" />} />
      <Route path="/blog/ads-that-pay-for-themselves" element={<BlogPost slug="ads-that-pay-for-themselves" />} />
      <Route path="/blog/a-barber-shop-at-full-capacity" element={<BlogPost slug="a-barber-shop-at-full-capacity" />} />
      <Route path="/blog/what-your-calendar-is-hiding" element={<BlogPost slug="what-your-calendar-is-hiding" />} />
      <Route path="/blog/software-for-a-property-business" element={<BlogPost slug="software-for-a-property-business" />} />
      {/* Finance, Ratings, Product store and Marketing all exist today — full feature pages. */}
      <Route path="/features/finance" element={<FeaturePage featureKey="finance" />} />
      <Route path="/features/reviews" element={<FeaturePage featureKey="reviews" />} />
      <Route path="/features/products" element={<FeaturePage featureKey="products" />} />
      <Route path="/features/marketing" element={<FeaturePage featureKey="marketing" />} />
      {/* Hidden for now — old/stray links fall back to the features hub (or its new home). */}
      <Route path="/features/ai" element={<Navigate to="/features" replace />} />
      <Route path="/features/google-calendar" element={<Navigate to="/features" replace />} />
      <Route path="/features/ads" element={<Navigate to="/features/marketing" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return <AppContent />;
}

export default App;
